/**
 * Expiry utility functions for CampMinder credentials.
 *
 * Brand guide §23.
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-28-credentials-page-expiry-banner-v3.html
 *
 * Consumed by:
 *  - CredentialExpiryBanner (top-of-page banner)
 *  - CredentialsTable (row-level border + inline chip)
 *  - Credentials page (filter for expiring rows)
 */

// ─── Expiry computation ────────────────────────────────────────────────────────

/**
 * Returns days until expiry from an ISO-8601 string.
 *
 * Rounding rules:
 *  - diffMs <= 0: expired — returns a non-positive integer (0 for exactly-now,
 *    negative for past). Uses Math.floor so "1 hour ago" → -1, not 0.
 *  - diffMs > 0: future — returns a positive integer, rounded up via Math.ceil
 *    so "23 hours left" → 1, not 0. This matches the user-visible phrasing
 *    ("expires in 1 day") rather than silently re-classifying same-day future
 *    expiries as already-expired.
 *
 * Returns null if the date string is absent or unparseable.
 */
export function computeDaysUntilExpiry(apiKeyExpiresAt: string | null | undefined): number | null {
  if (!apiKeyExpiresAt) return null
  const expiry = new Date(apiKeyExpiresAt)
  if (isNaN(expiry.getTime())) return null
  // Use Date.now() (not `new Date()`) so tests can freeze time via
  // jest.spyOn(Date, 'now') — `new Date()` reads the real clock even when
  // Date.now is mocked, which caused a flaky "expired right now" test.
  const now = Date.now()
  const diffMs = expiry.getTime() - now
  const dayMs = 1000 * 60 * 60 * 24
  // Expired (or exactly now): keep negative further-from-zero via floor.
  if (diffMs <= 0) return Math.floor(diffMs / dayMs)
  // Future: round up so "23 hours" → 1 day, matching the phrasing users see.
  return Math.ceil(diffMs / dayMs)
}

/**
 * Formats the expiry date for display. Returns a string like "May 11, 2026".
 * Falls back to the raw string if unparseable.
 */
export function formatExpiryDate(apiKeyExpiresAt: string | null | undefined): string {
  if (!apiKeyExpiresAt) return ''
  const d = new Date(apiKeyExpiresAt)
  if (isNaN(d.getTime())) return apiKeyExpiresAt
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * Expiry severity tier derived from days-until-expiry.
 *  - 'none'    — no warning (> 30 days or no expiry date)
 *  - 'warning' — expiring within 30 days (amber, dismissible above 7 days)
 *  - 'expired' — already expired (red, non-dismissible)
 */
export type ExpiryTier = 'none' | 'warning' | 'expired'

export function getExpiryTier(days: number | null): ExpiryTier {
  if (days === null) return 'none'
  if (days > 30) return 'none'
  if (days <= 0) return 'expired'
  return 'warning'
}
